/**
 * Inference Engine — Fill in NULL fields based on context.
 * GOLDEN RULE: NEVER override user-specified fields.
 * Only infer what the user didn't mention.
 */

import type { SceneGraph } from './sceneGraph';
import { isUserSpecified } from './sceneGraph';

type InferenceRule = {
  name: string;
  condition: (g: SceneGraph) => boolean;
  apply: (g: SceneGraph) => void;
};

// ─── Helper ───

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function inferField(graph: SceneGraph, path: string, value: unknown, target: any, key: string) {
  if (isUserSpecified(graph, path)) return; // NEVER override user intent
  if (target[key] !== null) return; // already set (by another rule)
  target[key] = value;
  graph.meta.inferredFields.push(path);
}

// ─── Inference Rules ───

const RULES: InferenceRule[] = [
  // ── Distance → Camera ──
  {
    name: 'far_distance_camera',
    condition: (g) => g.camera.distance === 'far',
    apply: (g) => {
      inferField(g, 'camera.shotType', 'wide', g.camera, 'shotType');
      inferField(g, 'camera.lens', '85mm', g.camera, 'lens');
      inferField(g, 'camera.angle', 'eye_level', g.camera, 'angle');
      inferField(g, 'camera.dof', 'deep', g.camera, 'dof');
    },
  },
  {
    name: 'close_distance_camera',
    condition: (g) => g.camera.distance === 'close',
    apply: (g) => {
      inferField(g, 'camera.shotType', 'close_up', g.camera, 'shotType');
      inferField(g, 'camera.lens', '85mm', g.camera, 'lens');
      inferField(g, 'camera.dof', 'shallow', g.camera, 'dof');
    },
  },
  {
    name: 'medium_distance_camera',
    condition: (g) => g.camera.distance === 'medium' || (!g.camera.distance && g.character.type !== null),
    apply: (g) => {
      inferField(g, 'camera.shotType', 'medium', g.camera, 'shotType');
      inferField(g, 'camera.lens', '50mm', g.camera, 'lens');
      inferField(g, 'camera.dof', 'moderate', g.camera, 'dof');
    },
  },

  // ── Genre → Lighting + Mood + Camera ──
  {
    name: 'horror_genre',
    condition: (g) => g.style.genre === 'horror' || g.character.type === 'zombie',
    apply: (g) => {
      inferField(g, 'lighting.type', 'dramatic', g.lighting, 'type');
      inferField(g, 'lighting.intensity', 'low_key', g.lighting, 'intensity');
      inferField(g, 'lighting.colorTemp', 'cool', g.lighting, 'colorTemp');
      inferField(g, 'style.mood', 'eerie', g.style, 'mood');
      inferField(g, 'style.genre', 'horror', g.style, 'genre');
      inferField(g, 'style.colorPalette', 'desaturated', g.style, 'colorPalette');
      inferField(g, 'environment.atmosphere', 'foggy', g.environment, 'atmosphere');
      inferField(g, 'character.skinDetail', 'detailed undead skin texture', g.character, 'skinDetail');
    },
  },
  {
    name: 'epic_genre',
    condition: (g) => g.style.genre === 'epic' || g.style.mood === 'heroic',
    apply: (g) => {
      inferField(g, 'camera.lens', '24mm', g.camera, 'lens');
      inferField(g, 'camera.shotType', 'wide', g.camera, 'shotType');
      inferField(g, 'camera.angle', 'low_angle', g.camera, 'angle');
      inferField(g, 'lighting.type', 'dramatic', g.lighting, 'type');
      inferField(g, 'lighting.intensity', 'high_key', g.lighting, 'intensity');
      inferField(g, 'style.mood', 'dramatic', g.style, 'mood');
      inferField(g, 'environment.atmosphere', 'volumetric haze', g.environment, 'atmosphere');
    },
  },
  {
    name: 'noir_genre',
    condition: (g) => g.style.genre === 'noir',
    apply: (g) => {
      inferField(g, 'lighting.type', 'dramatic', g.lighting, 'type');
      inferField(g, 'lighting.intensity', 'low_key', g.lighting, 'intensity');
      inferField(g, 'lighting.direction', 'side', g.lighting, 'direction');
      inferField(g, 'style.colorPalette', 'desaturated', g.style, 'colorPalette');
      inferField(g, 'style.mood', 'tense', g.style, 'mood');
      inferField(g, 'environment.time', 'night', g.environment, 'time');
    },
  },
  {
    name: 'romantic_genre',
    condition: (g) => g.style.genre === 'romantic',
    apply: (g) => {
      inferField(g, 'lighting.type', 'soft', g.lighting, 'type');
      inferField(g, 'lighting.colorTemp', 'warm', g.lighting, 'colorTemp');
      inferField(g, 'lighting.preset', 'golden_hour', g.lighting, 'preset');
      inferField(g, 'style.mood', 'warm', g.style, 'mood');
      inferField(g, 'style.colorPalette', 'warm_tones', g.style, 'colorPalette');
      inferField(g, 'camera.dof', 'shallow', g.camera, 'dof');
    },
  },
  {
    name: 'scifi_genre',
    condition: (g) => g.style.genre === 'sci-fi',
    apply: (g) => {
      inferField(g, 'lighting.type', 'neon', g.lighting, 'type');
      inferField(g, 'lighting.colorTemp', 'cool', g.lighting, 'colorTemp');
      inferField(g, 'style.colorPalette', 'teal_orange', g.style, 'colorPalette');
      inferField(g, 'environment.atmosphere', 'clean', g.environment, 'atmosphere');
    },
  },

  // ── Character type → Skin + defaults ──
  {
    name: 'warrior_character',
    condition: (g) => g.character.type === 'warrior' || g.character.type === 'knight' || g.character.type === 'samurai',
    apply: (g) => {
      inferField(g, 'character.pose', 'heroic stance', g.character, 'pose');
      inferField(g, 'style.mood', 'dramatic', g.style, 'mood');
      inferField(g, 'camera.angle', 'low_angle', g.camera, 'angle');
    },
  },

  // ── Time of day → Lighting ──
  {
    name: 'night_time',
    condition: (g) => g.environment.time === 'night' || g.environment.time === 'midnight',
    apply: (g) => {
      inferField(g, 'lighting.type', 'moonlight', g.lighting, 'type');
      inferField(g, 'lighting.intensity', 'low_key', g.lighting, 'intensity');
      inferField(g, 'lighting.colorTemp', 'cool', g.lighting, 'colorTemp');
      inferField(g, 'lighting.preset', 'blue_hour', g.lighting, 'preset');
    },
  },
  {
    name: 'golden_hour_time',
    condition: (g) => g.environment.time === 'golden_hour' || g.environment.time === 'sunset',
    apply: (g) => {
      inferField(g, 'lighting.type', 'soft', g.lighting, 'type');
      inferField(g, 'lighting.colorTemp', 'warm', g.lighting, 'colorTemp');
      inferField(g, 'lighting.preset', 'golden_hour', g.lighting, 'preset');
    },
  },

  // ── Weather → Atmosphere ──
  {
    name: 'rain_weather',
    condition: (g) => g.environment.weather === 'rain' || g.environment.weather === 'storm',
    apply: (g) => {
      inferField(g, 'environment.atmosphere', 'wet surfaces with rain streaks', g.environment, 'atmosphere');
      inferField(g, 'lighting.type', 'dramatic', g.lighting, 'type');
    },
  },
  {
    name: 'fog_weather',
    condition: (g) => g.environment.weather === 'fog',
    apply: (g) => {
      inferField(g, 'environment.atmosphere', 'thick fog with reduced visibility', g.environment, 'atmosphere');
      inferField(g, 'camera.dof', 'deep', g.camera, 'dof');
    },
  },

  // ── Emotion-specific camera ──
  {
    name: 'menacing_emotion',
    condition: (g) => g.character.emotion === 'menacing' || g.character.emotion === 'angry',
    apply: (g) => {
      inferField(g, 'camera.angle', 'low_angle', g.camera, 'angle');
      inferField(g, 'lighting.type', 'dramatic', g.lighting, 'type');
      inferField(g, 'lighting.direction', 'rim', g.lighting, 'direction');
    },
  },

  // ── Fallback defaults (last resort if nothing was inferred) ──
  {
    name: 'default_camera',
    condition: (g) => g.camera.lens === null,
    apply: (g) => {
      inferField(g, 'camera.lens', '50mm', g.camera, 'lens');
      inferField(g, 'camera.shotType', 'medium', g.camera, 'shotType');
      inferField(g, 'camera.angle', 'eye_level', g.camera, 'angle');
      inferField(g, 'camera.dof', 'moderate', g.camera, 'dof');
      inferField(g, 'camera.movement', 'static', g.camera, 'movement');
    },
  },
  {
    name: 'default_lighting',
    condition: (g) => g.lighting.type === null,
    apply: (g) => {
      inferField(g, 'lighting.type', 'natural', g.lighting, 'type');
      inferField(g, 'lighting.intensity', 'balanced', g.lighting, 'intensity');
      inferField(g, 'lighting.colorTemp', 'neutral', g.lighting, 'colorTemp');
    },
  },
  {
    name: 'default_render_style',
    condition: (g) => g.style.renderStyle === null,
    apply: (g) => {
      inferField(g, 'style.renderStyle', 'cinematic', g.style, 'renderStyle');
    },
  },
];

/**
 * Run the inference engine on a SceneGraph.
 * Returns a new SceneGraph with inferred fields filled in.
 * User-specified fields are NEVER overridden.
 */
export function runInference(input: SceneGraph): SceneGraph {
  // Deep clone to avoid mutation
  const graph: SceneGraph = JSON.parse(JSON.stringify(input));
  graph.meta.inferredFields = [];

  // Run all rules in order (order matters — genre rules before fallbacks)
  for (const rule of RULES) {
    if (rule.condition(graph)) {
      rule.apply(graph);
    }
  }

  return graph;
}
